import { z } from "zod";
export const StorylineSeedArticleSchema = z
    .object({
    url: z.string().url(),
    title: z.string().trim().min(1).max(280).optional(),
    publishedAt: z.string().datetime().optional(),
    domain: z.string().trim().min(1).optional(),
    quote: z.string().trim().max(200).optional(),
})
    .strict();
export const DeepResearchEvidenceSchema = z
    .object({
    url: z.string().url().optional(),
    title: z.string().trim().min(1).max(280).optional(),
    claim: z.string().trim().max(600).optional(),
    excerpt_hint: z.string().trim().max(600).optional(),
    excerpt: z.string().trim().max(600).optional(),
    published_at: z.string().datetime().optional(),
    publishedAt: z.string().datetime().optional(),
    source: z.string().trim().max(160).optional(),
})
    .passthrough();
export const DeepResearchStorylineSchema = z
    .object({
    headline: z.string().trim().min(1).max(280).optional(),
    domain: z.string().trim().max(64).optional(),
    impact_order: z.enum(["second", "third"]).optional(),
    path_of_impacts: z.array(z.string().trim().min(1)).optional(),
    exposures: z.array(z.string().trim().min(1)).optional(),
    time_horizon: z.enum(["now", "1-3m", "3-12m", "12m+"]).optional(),
    thesis: z.string().trim().max(1500).optional(),
    supporting_urls: z.array(z.string().url()).optional(),
    contradicting_urls: z.array(z.string().url()).optional(),
    supporting_events: z
        .array(z
        .object({
        source_headline: z.string().trim().min(1).max(280).optional(),
        source_date: z.string().trim().max(32).optional(),
        note: z.string().trim().max(600).optional(),
        url: z.string().url().optional(),
    })
        .strict())
        .optional(),
    importance_score: z.number().int().min(1).max(3).optional(),
    title: z.string().trim().min(1).max(280).optional(),
    summary: z.string().trim().max(1200).optional(),
    timeframe: z.string().trim().max(120).optional(),
    signal_strength: z.string().trim().max(32).optional(),
    affected_entities: z.array(z.string().trim().min(1)).optional(),
    risk_tags: z.array(z.string().trim().min(1)).optional(),
    supporting_evidence: z.array(DeepResearchEvidenceSchema).optional(),
    contradicting_evidence: z.array(DeepResearchEvidenceSchema).optional(),
    tempo: z.enum(["fast", "medium", "slow"]).optional(),
    order_of_impact: z.enum(["first", "second", "third"]).optional(),
    mechanism: z.string().trim().max(160).optional(),
    impact_direction: z
        .enum(["positive", "negative", "mixed", "ambiguous"])
        .optional(),
    horizon: z.enum(["now", "1-3m", "3-12m", "12m+"]).optional(),
    bridge_entities: z.array(z.string().trim().min(1)).optional(),
    exposure_keys: z.array(z.string().trim().min(1)).optional(),
    monitor_query: z.string().trim().max(400).optional(),
    novelty_against_known: z.number().min(0).max(1).optional(),
})
    .passthrough();
const StorylineClaimSchema = z
    .object({
    text: z.string().trim().min(1),
    importance: z.enum(["low", "medium", "high"]).optional(),
})
    .strict();
const StorylineImpactSchema = z
    .object({
    audience: z.string().trim().min(1),
    effect: z.string().trim().min(1),
})
    .strict();
export const STORYLINE_BUCKETS = [
    "regulatory",
    "political",
    "customers",
    "suppliers",
    "logistics",
    "market_trends",
    "mna",
    "themes",
    "financing",
    "general",
];
export const StorylineSeedDeepResearchSchema = z
    .object({
    storyline: DeepResearchStorylineSchema.optional(),
    researchNotes: z.string().trim().max(4000).nullable().optional(),
})
    .strict();
export const StorylineSeedSchema = z
    .object({
    schemaVersion: z.literal("storylineSeed.v1"),
    instrumentId: z.string().min(1),
    ticker: z.string().min(1),
    bucket: z.enum(STORYLINE_BUCKETS),
    headline: z.string().trim().min(1).max(160),
    summary: z.string().trim().min(1).max(600),
    claims: z.array(StorylineClaimSchema).min(1),
    impacts: z.array(StorylineImpactSchema).optional(),
    supportingArticles: z.array(StorylineSeedArticleSchema).min(1),
    contradictingArticles: z.array(StorylineSeedArticleSchema).optional(),
    confidence: z.number().min(0).max(1).optional(),
    objective: z.string().optional(),
    promptId: z.string().optional(),
    creditsUsed: z.number().nonnegative().optional(),
    origin: z.enum(["article", "deep_research", "monitor"]).optional(),
    tempo: z.enum(["fast", "medium", "slow"]).optional(),
    deepResearch: StorylineSeedDeepResearchSchema.optional(),
    createdAt: z.string().datetime(),
})
    .strict();
export const StorylineSeedEnvelopeSchema = z
    .object({
    seed: StorylineSeedSchema,
    seedRef: z.object({
        bucket: z.string().min(1),
        key: z.string().min(1),
        bytes: z.number().nonnegative(),
    }),
    articleHash: z.string().optional(),
    planHash: z.string().optional(),
})
    .strict();
